import React from 'react';
import {useSelector} from 'react-redux';

import {MODES} from '@/components/DeviceIcon/DeviceIcon';
import {MyDevicesIcon, RenameBlueIcon} from '@/components/Icons';
import {
    MY_DEVICES_PAGE_ABOUT_DEVICE_BUTTON_ARIA_LABEL,
    MY_DEVICES_PAGE_DIAGNOSE_ISSUE_BUTTON_ARIA_LABEL,
    MY_DEVICES_PAGE_RENAME_DEVICE_BUTTON_ARIA_LABEL,
    MY_DEVICES_PAGE_TIPS_AND_TUTORIALS_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import advanceDiagnosticService from '@/containers/AdvancedDiagnosticPage/advanceDiagnosticService';
import HolderDetectedBanner from '@/containers/DevicesPage/components/DeviceSettings/components/HolderDetectedBanner/HolderDetectedBanner';
import DeviceSettings from '@/containers/DevicesPage/components/DeviceSettings/DeviceSettings';
import useOnRenameClick from '@/containers/DevicesPage/components/MyDevicesView/hooks/useOnRenameClick';
import useFwuCheckEnabled from '@/hooks/fwu/useFwuCheckEnabled';
import useFwuManualChecker from '@/hooks/fwu/useFwuManualChecker';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import amplitudeDevicesPageTracking from '@/services/analytics/amplitude/amplitudeDevicesPageTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import appRouterService from '@/services/route/appRouterService';
import {selectCurrentProductInfo} from '@/state/selectors/device';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch} from '@/state/store';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners} from '@/views/WarningBanners';

import FirmwareUpdateIcon from '../../../../../components/Icons/FirmwareUpdateIcon';
import DeviceLock from '../../DeviceSettings/views/DeviceLock/DeviceLock';
import {useDeviceLockVisibility} from '../../DeviceSettings/views/DeviceLock/useDeviceLockVisibility';
import DevicesSettingsSection from '../../DevicesSettingsSection/DevicesSettingsSection';
import MyDevicesConnectButton from './MyDevicesConnectButton/MyDevicesConnectButton';
import {useConnectionFlow} from './MyDevicesConnectButton/useConnectionFlow';
import UpdateAvailableBanner from './UpdateAvailableBanner/UpdateAvailableBanner';

const MyDevicesManageDevice = () => {
    const isMobile = useIsMobileLayout();
    const device = useSelector(selectCurrentProductInfo);
    const onRenameClick = useOnRenameClick();
    const {showConnectButton} = useConnectionFlow();
    const isDeviceLockVisible = useDeviceLockVisibility();
    const isFwuCheckEnabled = useFwuCheckEnabled();
    const checkFwu = useFwuManualChecker();

    const {type, deviceSerialNumber} = device || {};
    const isDiagnosticEnabled = advanceDiagnosticService.isEnabled(type);

    const onAboutClick = () => {
        appRouterService.forwardToAboutDevicePage();
    };

    const onAdClick = () => {
        appRouterService.forwardToADPage();
    };

    const onTipsAndTutorialsClick = () => {
        amplitudeDevicesPageTracking.trackTipsAndTutorialsScreen();
        googleAnalyticsService.trackTipsAndTutorialsScreenView(type, deviceSerialNumber);
        appRouterService.forwardToTipsAndTutorialsList();
    };

    const onFwuUpdateClick = async () => {
        dispatch(showLoader());
        await checkFwu();
        dispatch(hideLoader());
        //TODO: redirect or show Software Update screen
    };

    return (
        <div className='flex h-full flex-col'>
            <HolderDetectedBanner className='px-4 md:px-8 ml:snap-start ml:pt-8' />
            <UpdateAvailableBanner className='px-4 pt-4 md:px-8 ml:snap-start ml:pt-8' />
            {isMobile && showConnectButton && (
                <DevicesSettingsSection.Group className='pt-4 ml:snap-start ml:pt-8'>
                    <DevicesSettingsSection.Buttons>
                        <MyDevicesConnectButton />
                    </DevicesSettingsSection.Buttons>
                </DevicesSettingsSection.Group>
            )}
            {isMobile && isDeviceLockVisible && (
                <DevicesSettingsSection.Group className='pt-4 ml:snap-start ml:pt-8'>
                    <DevicesSettingsSection.Buttons>
                        <DeviceLock />
                    </DevicesSettingsSection.Buttons>
                </DevicesSettingsSection.Group>
            )}
            <DeviceSettings className='pt-4 ml:snap-start ml:pt-8' />
            <DevicesSettingsSection.Group className='pt-4 ml:snap-start ml:pt-8'>
                <DevicesSettingsSection.Title itemName='manageYourIqos' />
                <DevicesSettingsSection.Buttons>
                    {isMobile && (
                        <DevicesSettingsSection.Button
                            itemName='renamemydevice'
                            ariaLabelKey={MY_DEVICES_PAGE_RENAME_DEVICE_BUTTON_ARIA_LABEL}
                            icon={RenameBlueIcon}
                            onClick={onRenameClick}
                            hasSeparator
                        />
                    )}
                    {isFwuCheckEnabled && (
                        <DevicesSettingsSection.Button
                            itemName='firmwareupdate'
                            icon={FirmwareUpdateIcon}
                            onClick={onFwuUpdateClick}
                            hasSeparator
                        />
                    )}
                    <DevicesSettingsSection.Button
                        itemName='aboutmydevice'
                        ariaLabelKey={MY_DEVICES_PAGE_ABOUT_DEVICE_BUTTON_ARIA_LABEL}
                        mode={MODES.ABOUT}
                        onClick={onAboutClick}
                        hasSeparator
                    />
                    {isDiagnosticEnabled && (
                        <DevicesSettingsSection.Button
                            itemName='diagnoseissue'
                            ariaLabelKey={MY_DEVICES_PAGE_DIAGNOSE_ISSUE_BUTTON_ARIA_LABEL}
                            mode={MODES.DIAGNOSTICS}
                            onClick={onAdClick}
                            hasSeparator
                        />
                    )}
                    <DevicesSettingsSection.Button
                        itemName='tipsandtutorials'
                        ariaLabelKey={MY_DEVICES_PAGE_TIPS_AND_TUTORIALS_BUTTON_ARIA_LABEL}
                        icon={MyDevicesIcon}
                        onClick={onTipsAndTutorialsClick}
                    />
                </DevicesSettingsSection.Buttons>
            </DevicesSettingsSection.Group>
            <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.DEVICES} containerClassName='mt-auto' />
        </div>
    );
};

export default MyDevicesManageDevice;
